/* eslint-disable max-lines-per-function */
import React from "react";
import { gql, useQuery } from "@apollo/client";
import { Form, Formik } from "formik";
import { Button, Checkbox, CircularProgress, FormControlLabel, FormGroup } from "@material-ui/core";
import { Modal } from "components/haven-design";
import { useSnackbar } from "components/haven-design/snackbar";
import { CheckboxWithErrorLabel } from "components/haven-design/form/checkbox";
import { InterestCsvFields as QueryRes, InterestCsvFieldsVariables as QueryVars } from "types/gql-types";
import logger from "services/logger";
import { DialogActions, DialogContent } from "components/haven-design/modal/dialog";
import { useInterestsTable } from "../interests-table/table-context";
import { useSidebarFilters, formatFilters } from "../sidebar-filters";
import { downloadCsv, lastExportFields, saveLastExportFields } from "./download-form-modal/utils";
import styles from "./download-form-modal.module.css";

const query = gql`
  query InterestCsvFields($interestListId: String!) {
    interestCsvFields(interestListId: $interestListId) {
      label,
      name,
    }
  }
`;

interface Props {
  defaultSorting?: string;
  isOpen: boolean;
  formIds: string[];
  allForms: boolean;
  interestListId: string;
  onRequestClose: () => void;
}

const copyAndSetAllValues = (values: Record<string, boolean>, newValue: boolean) => {
  const newObject = { ...values };

  Object.keys(newObject).forEach((i) => {
    newObject[i] = newValue;
  });

  return newObject;
};

const isChecked = (values: Record<string, boolean>) => Object.values(values).every(Boolean);

const isIndeterminate = (values: Record<string, boolean>) => {
  const vals = Object.values(values);
  return vals.some(Boolean) && !vals.every(Boolean);
};

const onClickSelectAll = (
  values: Record<string, boolean>,
  setValues: (
    values: React.SetStateAction<{[key: string]: boolean}>,
    shouldValidate?: boolean | undefined) => void,
) => {
  setValues(copyAndSetAllValues(values, !isChecked(values)), true);
};

const extendWithUnitSize = (fields: QueryRes["interestCsvFields"]) => {
  const copy = [...fields];
  const unitSizeIndex = fields.findIndex(({ name }) => name.startsWith("unit_size"));
  const remainingFields = copy.filter(({ name }) => !name.startsWith("unit_size"));
  const unitSizeField = {
    label: "Unit Size",
    name: "unit_size",
    __typename: "InterestCsvField",
  } as const;
  if (unitSizeIndex === -1) return copy;
  remainingFields.splice(unitSizeIndex, 0, unitSizeField);
  return remainingFields;
};

export const DownloadFormModal: React.FC<Props> = props => {
  const { hasSelectedAllInterests, selectedPageRange } = useInterestsTable();
  const { formIds: interestIds, interestListId } = props;
  const { openSnackbar } = useSnackbar();
  const {
    filterState,
    textFilter,
    interestListIds,
  } = useSidebarFilters();
  const [loading, setLoading] = React.useState(false);
  const {
    data: csvFieldsData,
    loading: csvFieldsLoading,
  } = useQuery<QueryRes, QueryVars>(query, { variables: { interestListId } });
  const availableFields = extendWithUnitSize(csvFieldsData?.interestCsvFields || []);

  if (!props.isOpen) return null;

  const preCheckedFields = lastExportFields(interestListId);

  const initialForm = availableFields
    .reduce((acc, { name }) => {
      // precheck all fields if last exported fields are not found
      acc[name] = preCheckedFields.length === 0 || preCheckedFields.includes(name);
      return acc;
    }, {} as Record<string, boolean>);

  const elasticQuery = {
    filters: formatFilters(filterState),
    textFilter,
    defaultSorting: props.defaultSorting,
  };

  const { from, to } = selectedPageRange;
  const selectedPages = from !== null || to !== null;
  const sendQuery = selectedPages || hasSelectedAllInterests;
  const selection = {
    "interest_ids": sendQuery ? null : interestIds,
    "elastic_query": sendQuery ? elasticQuery : null,
  };

  return (
    <Modal
      title="Choose Data to Export"
      isOpen={props.isOpen}
      onRequestClose={props.onRequestClose}
      fullWidth
      fullScreen
      scroll="paper"
    >
      <DialogContent>
        <Formik initialValues={initialForm} onSubmit={async (values) => {
          try {
            setLoading(true);
            const checked = Object.entries(values)
              .reduce((acc, [key, val]) => (val ? acc.concat(key) : acc), [] as string[]);

            const columns = availableFields
              .map(x => x.name)
              .filter(fieldName => checked.includes(fieldName));
            await downloadCsv(
              { interestListIds, columns },
              selectedPageRange,
              selection,
            );
            props.onRequestClose();
            saveLastExportFields(interestListId, checked);
          } catch (err) {
            if (err instanceof Error) logger.sendError(err);
            openSnackbar({ message: "Error downloading spreadsheet", status: "error" });
          } finally {
            setLoading(false);
          }
        }}>
          {({ values, setValues }) => (
            <Form id="export-csv">
              <FormGroup>
                <FormControlLabel control={
                  <Checkbox
                    indeterminate={isIndeterminate(values)}
                    checked={isChecked(values)}
                    onClick={() => onClickSelectAll(values, setValues)} />
                } label="Select All" />
              </FormGroup>
              {availableFields.map(({ name, label }, i) => (
                <FormGroup key={i}>
                  <CheckboxWithErrorLabel
                    name={name}
                    label={label}
                    className={styles.indentedCheckbox}
                  />
                </FormGroup>
              ))}

            </Form>
          )}
        </Formik>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={props.onRequestClose} style={{ marginRight: 20 }} size="large">
          Cancel
        </Button>
        {(loading) ? <div style={{ textAlign: "center", width: 95 }}><CircularProgress /></div> : (
          <Button form="export-csv" type="submit" variant="contained" color="primary" disabled={csvFieldsLoading} size="large">
            Export
          </Button>
        )}
      </DialogActions>
    </Modal>
  );
};
